// Explore with SQL (plan step 3.5b; D95; Console D, APS-317): one read-only SELECT over the
// project's own telemetry, through run_query, the same query agents send. What it may read and how
// long it may run are ClickHouse's (the API's query route); this page shows the limits, the
// tables, a few examples, and every error class in plain words. Rows are the app's data: shown as
// text, never as markup, never translated. The draft and the recent queries are kept in this
// browser per project, and nowhere else. A query that ran can become a dashboard panel
// (AddToDashboard). The Metrics and Traces builders (APS-198) are tabs under the same head; Logs
// is its own page (D103 #14, APS-327).

import { useQuery } from '@tanstack/react-query'
import { Link, useSearch } from '@tanstack/react-router'
import { type KeyboardEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { QueryError, querySchemaQuery, useRunQuery } from '../api/queries'
import type { Dashboard, QueryResult } from '../api/types'
import { AddToDashboard } from '../components/AddToDashboard'
import { Btn, Kbd, PageHead, Section, Table, Tabs } from '../components/d'
import { Icon } from '../components/icons'
import { ErrorState, Loading } from '../components/states'
import { type MessageId, type Translator, useI18n } from '../i18n'
import { cellText, duration } from '../lib/format'
import { isNumericType } from '../lib/panels'
import { ExploreMetrics } from './ExploreMetrics'
import { ExploreTraces } from './ExploreTraces'
import { useProjectParams } from './params'
import { EXPLORE_TABS, type ExploreSearch } from './search'

/** The route's limits (apps/api read/query.ts, migrations/clickhouse query_access). */
export const MAX_SQL_LENGTH = 10_000

export const TABLES = [
  'logs',
  'spans',
  'metric_histograms',
  'metric_values',
  'session_summaries',
  'issue_occurrences',
] as const

const EXAMPLES: { key: MessageId; days: number; sql: string }[] = [
  {
    // As /releases counts it: one row per session (the table merges its summaries), started in
    // the range, crashed when its last outcome is `crashed`.
    key: 'explore.example.crashFree',
    days: 7,
    sql: `-- Crash-free sessions by release, last 7 days
SELECT release,
  count() AS sessions,
  countIf(outcome = 'crashed') AS crashed,
  round(100 * (1 - crashed / sessions), 2) AS crash_free_pct
FROM (
  SELECT any(service_version) AS release, argMaxMerge(outcome) AS outcome
  FROM apsio.session_summaries
  WHERE last_seen_ts > now() - INTERVAL 7 DAY
  GROUP BY app_id, session_id
  HAVING min(start_ts) > now() - INTERVAL 7 DAY
)
GROUP BY release
ORDER BY release DESC
LIMIT 20`,
  },
  {
    key: 'explore.example.screens',
    days: 7,
    sql: `SELECT attributes['app.screen.name'] AS screen,
  count() AS loads,
  quantile(0.9)(duration_ns / 1e6) AS ttid_p90_ms
FROM apsio.spans
WHERE span_name = 'app.screen.load' AND ts > now() - INTERVAL 7 DAY
GROUP BY screen
ORDER BY ttid_p90_ms DESC
LIMIT 20`,
  },
  {
    key: 'explore.example.failures',
    days: 7,
    sql: `SELECT http_method, http_template, count() AS failed
FROM apsio.spans
WHERE http_template != '' AND http_status >= 500 AND ts > now() - INTERVAL 7 DAY
GROUP BY http_method, http_template
ORDER BY failed DESC
LIMIT 20`,
  },
  {
    key: 'explore.example.events',
    days: 1,
    sql: `SELECT event_name, count() AS records
FROM apsio.logs
WHERE ts > now() - INTERVAL 1 DAY
GROUP BY event_name
ORDER BY records DESC
LIMIT 20`,
  },
]

/** A first look at a table: its latest rows. */
const previewOf = (table: string) => `SELECT *\nFROM ${table}\nLIMIT 100`

const draftKey = (projectId: string) => `apsio.explore.sql.${projectId}`
const recentKey = (projectId: string) => `apsio.explore.recent.${projectId}`
/** How many recent queries this browser keeps per project. */
const RECENT = 5

function readDraft(projectId: string): string | null {
  try {
    return window.localStorage.getItem(draftKey(projectId))
  } catch {
    return null
  }
}

/** Keeps the SQL as this project's Explore draft (a dashboard's panel opens there this way). */
export function saveDraft(projectId: string, sql: string): void {
  try {
    window.localStorage.setItem(draftKey(projectId), sql)
  } catch {
    // Private windows and blocked storage: the draft just isn't kept.
  }
}

interface Recent {
  sql: string
  at: string
  rows: number
}

function readRecent(projectId: string): Recent[] {
  try {
    const list: unknown = JSON.parse(window.localStorage.getItem(recentKey(projectId)) ?? '[]')
    return Array.isArray(list)
      ? list
          .filter(
            (x): x is Recent =>
              typeof x?.sql === 'string' &&
              typeof x?.at === 'string' &&
              typeof x?.rows === 'number',
          )
          .slice(0, RECENT)
      : []
  } catch {
    return []
  }
}

function saveRecent(projectId: string, list: Recent[]): void {
  try {
    window.localStorage.setItem(recentKey(projectId), JSON.stringify(list))
  } catch {
    // As the draft: not kept.
  }
}

function bytes(i18n: Translator, n: number): string {
  if (n < 1_000) return `${i18n.number(n)} B`
  if (n < 1_000_000) return `${i18n.number(n / 1_000, { maximumFractionDigits: 1 })} kB`
  if (n < 1_000_000_000) return `${i18n.number(n / 1_000_000, { maximumFractionDigits: 1 })} MB`
  return `${i18n.number(n / 1_000_000_000, { maximumFractionDigits: 1 })} GB`
}

/** Every error class the route answers with, in the reader's words (never ClickHouse's). */
export function errorMessage(t: Translator['t'], error: unknown): string {
  if (!(error instanceof QueryError)) return t('state.failed')
  switch (error.code) {
    case 'query_syntax':
      return error.position
        ? t('explore.error.syntaxAt', { position: error.position })
        : t('explore.error.syntax')
    case 'query_not_allowed':
      return t('explore.error.notAllowed', { tables: TABLES.map((x) => `apsio.${x}`).join(', ') })
    case 'query_limit':
      return t('explore.error.limit')
    case 'query_busy':
      return t('explore.error.busy')
    case 'rate_limited':
      return t('explore.error.rate')
    case 'plan_required':
      return t('explore.error.plan')
    case 'forbidden':
      return t('explore.error.forbidden')
    case 'erasing':
      return t('explore.error.erasing')
    case 'query_failed':
      return error.clickhouseError
        ? t('explore.error.failedClass', { error: error.clickhouseError })
        : t('explore.error.failed')
    default:
      return error.status === 503 ? t('explore.error.unavailable') : t('state.failed')
  }
}

/** The errors the plan decides: they link to it. */
const PLAN_ERRORS = new Set(['plan_required', 'rate_limited'])

const KEYWORDS =
  /\b(SELECT|FROM|WHERE|PREWHERE|GROUP BY|ORDER BY|LIMIT|OFFSET|HAVING|AND|OR|NOT|AS|DESC|ASC|INTERVAL|DAY|HOUR|MINUTE|WEEK|MONTH|IN|ON|USING|JOIN|LEFT|INNER|ARRAY JOIN|WITH|DISTINCT|BETWEEN|LIKE|ILIKE|IS|NULL|CASE|WHEN|THEN|ELSE|END|UNION ALL|SETTINGS|FORMAT)\b/gi

/** SQL with its keywords, strings and comments marked (.k .s .cm), for the editor's backdrop. */
export function highlight(sql: string): ReactNode[] {
  const out: ReactNode[] = []
  const words = (text: string) => {
    let i = 0
    for (const m of text.matchAll(KEYWORDS)) {
      if (m.index > i) out.push(text.slice(i, m.index))
      out.push(
        <span key={out.length} className="k">
          {m[0]}
        </span>,
      )
      i = m.index + m[0].length
    }
    if (i < text.length) out.push(text.slice(i))
  }
  let i = 0
  for (const m of sql.matchAll(/--[^\n]*|'(?:[^'\\]|\\.)*'?/g)) {
    words(sql.slice(i, m.index))
    out.push(
      <span key={out.length} className={m[0].startsWith('--') ? 'cm' : 's'}>
        {m[0]}
      </span>,
    )
    i = m.index + m[0].length
  }
  words(sql.slice(i))
  return out
}

/** Explore › SQL (D95): run_query, in the mock's code window. */
function ExploreSql() {
  const i18n = useI18n()
  const { t, number } = i18n
  const { orgId, projectId } = useProjectParams()
  const run = useRunQuery(projectId)
  const schema = useQuery(querySchemaQuery(projectId))
  const [sql, setSql] = useState(() => readDraft(projectId) ?? EXAMPLES[0]?.sql ?? '')
  const [recent, setRecent] = useState(() => readRecent(projectId))
  const [adding, setAdding] = useState(false)
  const [added, setAdded] = useState<Dashboard | null>(null)
  const editor = useRef<HTMLTextAreaElement>(null)
  const ids = { sql: useId(), hint: useId(), add: useId() }

  useEffect(() => saveDraft(projectId, sql), [projectId, sql])

  const submit = () => {
    if (!sql.trim() || run.isPending || sql.length > MAX_SQL_LENGTH) return
    setAdding(false)
    setAdded(null)
    run.mutate(sql, {
      onSuccess: (result) => {
        const next = [
          { sql, at: new Date().toISOString(), rows: result.row_count },
          ...readRecent(projectId).filter((x) => x.sql !== sql),
        ].slice(0, RECENT)
        saveRecent(projectId, next)
        setRecent(next)
      },
    })
  }
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      submit()
    }
  }
  const use = (text: string) => {
    setSql(text)
    run.reset()
    setAdding(false)
    setAdded(null)
    editor.current?.focus()
  }
  // A syntax error's position: the caret goes there, so the reader sees where.
  useEffect(() => {
    const error = run.error
    if (!(error instanceof QueryError) || !error.position || !editor.current) return
    const at = Math.max(0, Math.min(sql.length, error.position - 1))
    editor.current.focus()
    editor.current.setSelectionRange(at, Math.min(sql.length, at + 1))
  }, [run.error, sql.length])

  const result = run.data
  const tooLong = sql.length > MAX_SQL_LENGTH
  const ran = EXAMPLES.find((x) => x.sql === run.variables)
  const lines = sql.split('\n').length
  const planLink = (
    <Link
      className="ul"
      to="/o/$orgId/p/$projectId/settings/$section"
      params={{ orgId, projectId, section: 'plan' }}
    >
      {t('explore.plans')}
    </Link>
  )

  return (
    <div className="grid2">
      <div className="sections">
        <form
          className="code code-d ex-ed"
          aria-label={t('explore.query')}
          onSubmit={(e) => {
            e.preventDefault()
            submit()
          }}
        >
          <div className="ex-ed-bar">
            <label htmlFor={ids.sql}>
              <b>{t('explore.sql')}</b>
            </label>
            <span translate="no">apsio.*</span>
            <button
              type="submit"
              className="ex-run"
              disabled={!sql.trim() || tooLong || run.isPending}
            >
              {run.isPending ? t('explore.running') : t('explore.run')}
              <span aria-hidden="true">
                <Kbd>⌘</Kbd>
                <Kbd>↵</Kbd>
              </span>
            </button>
          </div>
          <div className="ex-ed-body">
            <div className="ex-gut" aria-hidden="true">
              {Array.from({ length: lines }, (_, i) => i + 1).join('\n')}
            </div>
            <div className="ex-area">
              <pre aria-hidden="true" translate="no">
                {highlight(sql)}
                {/* A last empty line still takes its height. */}
                {sql.endsWith('\n') || !sql ? ' ' : null}
              </pre>
              <textarea
                id={ids.sql}
                ref={editor}
                wrap="off"
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
                translate="no"
                aria-describedby={ids.hint}
                aria-invalid={tooLong || run.error instanceof QueryError || undefined}
                value={sql}
                onChange={(e) => setSql(e.target.value)}
                onKeyDown={onKeyDown}
              />
            </div>
          </div>
          <div className="ex-ed-foot" id={ids.hint}>
            <span className={tooLong ? 'ex-over' : undefined}>
              {t('explore.length', { length: number(sql.length), max: number(MAX_SQL_LENGTH) })}
            </span>
            <span>{t('explore.filterTs')}</span>
          </div>
        </form>

        <Section title={t('explore.results')} meta={ran ? t(ran.key) : undefined}>
          {run.isPending ? (
            <Loading label={t('explore.running')} />
          ) : run.isError ? (
            <p role="alert" className="notice error">
              {errorMessage(t, run.error)}
              {run.error instanceof QueryError && PLAN_ERRORS.has(run.error.code) ? (
                <> {planLink}</>
              ) : null}
            </p>
          ) : !result ? (
            <p className="ex-none">
              <b>{t('explore.none')}</b> {t('explore.noneBody')}
            </p>
          ) : (
            <>
              <div className="ex-res-h">
                <span className="ex-stats">
                  <b>{t('explore.rows', { rows: result.row_count })}</b>
                  {t('explore.stats', {
                    elapsed: duration(i18n, result.stats.elapsed_ms),
                    read: number(result.stats.rows_read),
                    bytes: bytes(i18n, result.stats.bytes_read),
                  })}
                </span>
                {run.variables && result.columns.length > 0 ? (
                  <span className="ex-acts">
                    <Btn
                      size="sm"
                      aria-expanded={adding}
                      aria-controls={adding ? ids.add : undefined}
                      onClick={() => {
                        setAdding(!adding)
                        setAdded(null)
                      }}
                    >
                      <Icon name="plus" />
                      {t('dash.add.button')}
                    </Btn>
                  </span>
                ) : null}
              </div>
              {added ? (
                <p role="status" className="notice ok ex-note">
                  {t('dash.add.done', { name: added.name })}{' '}
                  <Link
                    className="ul"
                    to="/o/$orgId/p/$projectId/dashboards"
                    params={{ orgId, projectId }}
                    search={{ board: added.id }}
                  >
                    {t('dash.add.open')}
                  </Link>
                </p>
              ) : null}
              {adding && run.variables ? (
                <AddToDashboard
                  id={ids.add}
                  sql={run.variables}
                  columns={result.columns}
                  title={ran ? t(ran.key) : ''}
                  onCancel={() => setAdding(false)}
                  onAdded={(board) => {
                    setAdding(false)
                    setAdded(board)
                  }}
                />
              ) : null}
              <Results result={result} />
            </>
          )}
        </Section>

        {recent.length > 0 ? (
          <Section title={t('explore.recent')} meta={t('explore.recentSub')} minor>
            <ul className="rl ex-recent">
              {recent.map((x) => (
                <li key={x.sql}>
                  <button
                    type="button"
                    className="ul ex-rc"
                    translate="no"
                    title={x.sql}
                    onClick={() => use(x.sql)}
                  >
                    {x.sql
                      .replace(/^--[^\n]*\n/, '')
                      .replace(/\s+/g, ' ')
                      .trim()}
                  </button>
                  <span>
                    {i18n.relative(x.at)} · {t('explore.rows', { rows: x.rows })}
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        ) : null}
      </div>

      <div className="sections">
        <Section
          title={t('explore.tables')}
          meta={schema.data ? number(schema.data.tables.length) : undefined}
        >
          {schema.isPending ? (
            <Loading />
          ) : schema.isError ? (
            <ErrorState error={schema.error} retry={() => schema.refetch()} />
          ) : (
            <ul className="ex-tbl">
              {schema.data.tables.map((table) => (
                <li key={table.name}>
                  <details>
                    <summary>
                      <code translate="no">{table.name.replace(/^apsio\./, '')}</code>
                      <span className="dim">
                        {t('explore.columnCount', { count: table.columns.length })}
                      </span>
                    </summary>
                    <ul
                      className="ex-cols"
                      aria-label={t('explore.columnsOf', { table: table.name })}
                    >
                      {table.columns.map((c) => (
                        <li key={c.name} translate="no">
                          <span>{c.name}</span> <span>{c.type}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                  <Btn
                    variant="ghost"
                    size="sm"
                    aria-label={t('explore.previewOf', { table: table.name })}
                    onClick={() => use(previewOf(table.name))}
                  >
                    {t('explore.preview')}
                  </Btn>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <Section title={t('explore.examples')}>
          <ul className="ex-ex">
            {EXAMPLES.map((example) => (
              <li key={example.key}>
                <button
                  type="button"
                  className={sql === example.sql ? 'ul on' : 'ul'}
                  aria-pressed={sql === example.sql}
                  onClick={() => use(example.sql)}
                >
                  {t(example.key)}
                </button>
                <code>{t('explore.days', { days: example.days })}</code>
              </li>
            ))}
          </ul>
        </Section>

        <Section title={t('explore.limits')} meta={t('explore.limitsSub')}>
          <ul className="ex-lim">
            {(
              [
                ['explore.limit.statements', 'explore.limit.statementsV'],
                ['explore.limit.time', 'explore.limit.timeV'],
                ['explore.limit.result', 'explore.limit.resultV'],
                ['explore.limit.read', 'explore.limit.readV'],
                ['explore.limit.concurrent', 'explore.limit.concurrentV'],
                ['explore.limit.rate', 'explore.limit.rateV'],
              ] as const
            ).map(([k, v]) => (
              <li key={k}>
                <span>{t(k)}</span>
                <b>{t(v)}</b>
              </li>
            ))}
          </ul>
          <p className="ex-foot">
            {t('explore.limitsFoot')} {planLink}
          </p>
        </Section>
      </div>
    </div>
  )
}

function Results({ result }: { result: QueryResult }) {
  const { t } = useI18n()
  if (result.columns.length === 0) return <p className="ex-none">{t('explore.noColumns')}</p>
  const numeric = result.columns.map((c) => isNumericType(c.type))
  const last = result.columns.length - 1
  // The mock's cells: the first flush left in ink, numbers right, the last flush right.
  const cls = (i: number) =>
    [i === 0 ? 'f' : numeric[i] ? 'num' : '', i === last && i > 0 ? 'last' : '']
      .filter(Boolean)
      .join(' ') || undefined
  return (
    <>
      {result.truncated ? <p className="notice ex-note">{t('explore.truncated')}</p> : null}
      {result.rows.length === 0 ? (
        <p className="ex-none">{t('explore.noRows')}</p>
      ) : (
        <Table label={t('explore.resultRows')} className="ex-res">
          <thead>
            <tr>
              {result.columns.map((c, i) => (
                // Column names can repeat (SELECT 1, 1): the position keeps the key unique.
                // biome-ignore lint/suspicious/noArrayIndexKey: columns never reorder
                <th key={i} scope="col" translate="no" className={cls(i)}>
                  {c.name}
                  <span className="ex-ty">{c.type}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {result.rows.map((row, r) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: a result's rows never reorder
              <tr key={r}>
                {row.map((value, i) => {
                  const { text, isNull } = cellText(value)
                  return (
                    // biome-ignore lint/suspicious/noArrayIndexKey: cells follow the columns
                    <td key={i} translate="no" className={cls(i)}>
                      {isNull ? <span className="dim">{text}</span> : text}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  )
}

/** Explore: SQL (the default) and the builders (APS-198: Metrics, Traces), as tabs kept in the address. */
export function ExplorePage() {
  const { t } = useI18n()
  const search = useSearch({ strict: false }) as ExploreSearch
  const tab = search.tab ?? 'sql'
  const label = {
    metrics: t('explore.tab.metrics'),
    traces: t('explore.tab.traces'),
    sql: t('explore.tab.sql'),
  }
  const meta = {
    metrics: t('explore.metrics.meta'),
    traces: t('explore.traces.meta'),
    sql: t('explore.meta'),
  }
  return (
    <main id="main" className="pg ex-pg">
      <PageHead title={t('explore.title')} meta={meta[tab]} />
      <Tabs label={t('explore.tabs')}>
        {EXPLORE_TABS.map((id) => (
          <Link
            key={id}
            to="."
            search={{
              ...(search.app ? { app: search.app } : {}),
              ...(id === 'sql' ? {} : { tab: id }),
            }}
            // The router marks a link current on a partial search match (SQL's empty search
            // would match every tab): exact here, and the tab's own mark for a URL with more.
            activeOptions={{ exact: true }}
            aria-current={tab === id ? 'page' : undefined}
          >
            {label[id]}
          </Link>
        ))}
      </Tabs>
      {tab === 'metrics' ? (
        <ExploreMetrics />
      ) : tab === 'traces' ? (
        <ExploreTraces />
      ) : (
        <ExploreSql />
      )}
    </main>
  )
}
